import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HistoryIcon from "@mui/icons-material/History";
import ReplayIcon from "@mui/icons-material/Replay";
import EditIcon from "@mui/icons-material/Edit";
import FlagIcon from "@mui/icons-material/Flag";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import { useActiveShow } from "common-fe";
import { useTranslation } from "i18n-fe";
import { RIDE_STATUSES, DERIVED_RIDE_STATUSES, isClassPlaced } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useFederationResults } from "../hooks/useFederationResults";
import { classNumberLabel, classDayKey, sortByClassNumber, formatVenueLocalDateTime } from "../util/classDisplay";
import ClassLink from "../components/ClassLink";

// #751 — scoreadmin's "Publish Results" section: the human-in-the-loop UI over
// report-api's USEF/USDF federation endpoints (#744-#750, #952's client).
// See docs/score/federation-results-publishing.md. Backend + client are
// already built; this page wires the flow described in the card:
//   1. comp year / USEF comp id / per-class section — all read-only, sourced
//      from the show/class records (#1004-#1006 stamp them at reconcile;
//      #1007 removed the operator setup form + section dropdown + the
//      Championship checkbox — IsChampionship is always false, matching
//      manager-api usef.js:205)
//   2. preview with missing-id warnings + manual override
//   3. publish (manual, confirm-before-submit)
//   4. per-class status/history + corrections

// #1086 — trigger a browser download of the USDF ERSS .dat content that
// came back from the "download" deliver mode (same text/csv content the
// "email" mode attaches, per usdf-erss-publish.js's one serializer call).
function triggerBrowserDownload(filename, content) {
  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// #1039 — a class is eligible for the show-level Publish walk while it's
// still in its pre-upload state (no status row yet, or explicitly DRAFT).
// Once UPLOADED/SUBMITTED/CORRECTED it's already published and the walk
// skips it (re-publishing is the one deliberate per-row action, below).
function classCanUpload(status) {
  return !status || status === "DRAFT";
}
// A class can only be deleted (and therefore re-published — delete then
// re-upload) while it's UPLOADED and not yet SUBMITTED/CORRECTED —
// federation-submission-state.js's ALLOWED_TRANSITIONS only allows
// UPLOADED -> DRAFT "pre-submit"; SUBMITTED/CORRECTED classes are final.
function classCanRepublish(status) {
  return status === "UPLOADED";
}

// #1040 — the corrections counterpart: once a class is SUBMITTED (or already
// CORRECTED — federation-submission-state.js allows CORRECTED -> CORRECTED,
// a class can be amended more than once), the only legal edit path is USEF's
// submitcorrectedclass (whole class). USDF has no correction API at all
// (docs/score/federation-results-publishing.md D-E/§7.4) — a USDF fix is a
// manual re-email of the .dat file, outside this page's scope.
function classCanCorrect(status) {
  return status === "SUBMITTED" || status === "CORRECTED";
}

// #1041 AC3 — Mark for re-publish is available on any already-published
// class (UPLOADED/SUBMITTED/CORRECTED), independent of classCanRepublish/
// classCanCorrect above (those gate the underlying USEF transition; this
// only flags the row for getFederationShowState's stale derive, so it
// applies both pre- and post-finalize — an operator can force it either way).
function classCanMarkStale(status) {
  return status === "UPLOADED" || status === "SUBMITTED" || status === "CORRECTED";
}

// #1042 — a class isn't publishable until it's actually done. Checked in this
// order because an open ride also can't have a placing yet, so surfacing "N
// rides not yet closed" is the more actionable of the two possible reasons:
//   1. every non-scratched ride must be "closed" — CONFIRMED or FINAL (#340's
//      classNeedsPlacings bar; DERIVED_RIDE_STATUSES.COMPLETED is still
//      partial, only some of the expected judges have signed, #791), a
//      SCRATCHED ride is resolved and never blocks (deriveRideStatus/
//      ride-status.js).
//   2. the class must be placed — isClassPlaced (score-common, #703): every
//      ride has a `placing`, is SCRATCHED, or is terminated (#2335 — #2069
//      forbids ever giving a terminated ride a placing, so it can only ever
//      resolve this way). Same derivation the rider portal gates on, so
//      "placed" never has two different meanings in this repo.
// Returns null once both hold, else { reason: "openRides" | "notPlaced", count }
// for the fold-out (count only meaningful for openRides).
function classIncompleteInfo(rides, rideStatusByRideId) {
  if (!rides || rides.length === 0) return null; // #1011 already hides 0-ride classes by default
  const openRides = rides.filter((r) => {
    if (r.status === RIDE_STATUSES.SCRATCHED) return false;
    const derived = rideStatusByRideId[r.id];
    return derived !== DERIVED_RIDE_STATUSES.CONFIRMED && derived !== DERIVED_RIDE_STATUSES.FINAL;
  });
  if (openRides.length > 0) return { reason: "openRides", count: openRides.length };
  if (!isClassPlaced(rides)) return { reason: "notPlaced", count: 0 };
  return null;
}

// #1110 — usef-serializer's buildJudgeEntry pushes one issue string per
// unresolved judge sheet (usef-serializer.js:218-223), verbatim like:
// "ride ... (CNO ...): judge Koch Gurney (position C) has no USEF ID —
// Judge.USEFID cannot be 0; resolve via #746 lookup or a manual override
// before uploading". A class with N unplaced rides sharing the same
// unresolved judge produces N copies of the same {name, position} — dedupe
// by position (one override box per booth position, matching how the class
// is actually judged) so the retry UI doesn't repeat itself per ride. The
// name is a "Koch Gurney" combined-box display name in FVD's real case
// (#1110) — rendered as-is, never split/validated, since that's not garbled
// data, just two judges sharing one box.
const JUDGE_ISSUE_RE = /judge (.+?) \(position ([A-Za-z0-9]+)\) has no USEF ID/;
function judgeIssuesFromIssues(issues) {
  const byPosition = new Map();
  for (const issue of issues || []) {
    const m = JUDGE_ISSUE_RE.exec(issue);
    if (m && !byPosition.has(m[2])) byPosition.set(m[2], { name: m[1], position: m[2] });
  }
  return [...byPosition.values()];
}

// #1107 — the class error display rewrite: a generic banner (no counts, no
// raw USEF strings) up top, with a plain-language breakdown grouped by ride
// below it. USEF's own /results/* 409 rejection text (parseUsefResultErrors,
// usef-submitter.js) tags each per-ride segment with a trailing
// "(on result {rideId}).", which is exactly the raw fvd:ride:... noise the
// card complains about — stripped before display, never rendered verbatim.
const RESULT_IDENTIFIER_SUFFIX_RE = /\s*\(on result [^)]*\)\.?\s*$/i;
function stripResultIdentifier(message) {
  return (message || "").replace(RESULT_IDENTIFIER_SUFFIX_RE, "").trim();
}

// Merges BOTH error sources a class can carry into one ride-grouped list:
//   - 400 usef_validation_error: classification.structuredIssues (parallel
//     to the untouched prose issues[] — usef-serializer.js's pushIssue),
//     already carrying rideId/cno/rider/horse at push time.
//   - 409 usef_rejected: classification.resultErrors (rideId + USEF's own
//     message, #1010) — resolved to cno/rider/horse via this class's
//     already-loaded rides (same join the old per-row Alert used).
// A judgeUsefId issue is skipped here — it already gets its own dedicated
// retry Alert with an inline override box (#1110, judgeIssuesFromIssues)
// right above this list, so repeating it here would just be noise.
// classification.classError only joins the general "Class:" bucket when
// there's no structuredIssues breakdown to explain it (the 400 path's
// classError is just err.message — the SAME issues joined into one string —
// so showing it too would re-dump the wall of text this card removes).
function groupClassErrors(classification, rides, t) {
  const classKey = "__class__";
  const groups = new Map();
  const ensureGroup = (key, meta) => {
    if (!groups.has(key)) groups.set(key, { ...meta, messages: [] });
    return groups.get(key);
  };

  for (const issue of classification?.structuredIssues || []) {
    if (issue.field === "judgeUsefId") continue;
    const key = issue.scope === "class" ? classKey : issue.rideId || classKey;
    ensureGroup(key, { rideId: issue.rideId, cno: issue.cno, rider: issue.rider, horse: issue.horse }).messages.push(
      issue.message
    );
  }

  for (const err of classification?.resultErrors || []) {
    const ride = (rides || []).find((r) => r.id === err.rideId);
    const key = err.rideId || classKey;
    ensureGroup(key, {
      rideId: err.rideId,
      cno: ride?.competitorNumber ?? null,
      rider: ride?.riderName ?? null,
      horse: ride?.horseName ?? null,
    }).messages.push(stripResultIdentifier(err.message));
  }

  if (classification?.classError && !(classification?.structuredIssues?.length > 0)) {
    ensureGroup(classKey, { rideId: null, cno: null, rider: null, horse: null }).messages.push(
      classification.classError
    );
  }

  return [...groups.entries()].map(([key, group]) => ({
    key,
    heading:
      key === classKey
        ? t("publishResults.errorGroupClass")
        : group.cno != null
          ? t("publishResults.errorGroupRide", {
              cno: group.cno,
              rider: group.rider || t("publishResults.unknownRider"),
              horse: group.horse || t("publishResults.unknownHorse"),
            })
          : t("publishResults.errorGroupRideUnknown", { rideId: group.rideId }),
    messages: group.messages,
  }));
}

// #1103 — single per-row status badge over the new 5-state model
// (incomplete/ready/publishing/published/error). skip is a separate flag
// (rendered as its own chip alongside, see the class-row render below) and
// no longer collapses the real state into a distinct "Skipped" badge — a
// skipped class still shows what it genuinely is underneath.
//
// #1040 — "corrected" (raw status CORRECTED) used to be its own derived
// state; it's now folded into "published" (both are a published,
// good-standing class post-finalize too) — the class-detail panel below
// still calls out a CORRECTED class's own message using the raw status, so
// that distinction isn't lost, just moved out of the top-level state name.
//
// #1103 — "needs-republish"/"needs-correction" (the stale counterparts) are
// gone as separate states: a published class that's gone stale (fingerprint
// drift or the manual "Mark for re-publish" override) now renders as
// published + a loud amber "out of date" badge — AC5's single combined
// signal, whether that staleness is pre- or post-finalize.
function statusBadge(t, derivedState, stale) {
  switch (derivedState) {
    case "incomplete":
      return { label: t("publishResults.incompleteChip"), color: "info" };
    case "publishing":
      return { label: t("publishResults.publishingChip"), color: "info", variant: "outlined" };
    case "error":
      // #1107 — no count on the chip either: "N error(s)" was one more spot
      // this card's "no counts, no raw strings" banner rule applies to. The
      // real detail lives in the grouped-by-ride breakdown below, once
      // expanded — this chip is just the row's at-a-glance status.
      return { label: t("publishResults.errorsChip"), color: "error" };
    case "published":
      return stale
        ? { label: t("publishResults.publishedStaleChip"), color: "warning" }
        : { label: t("publishResults.publishedChip"), color: "success" };
    default:
      return { label: t("publishResults.readyToPublishChip"), color: "success", variant: "outlined" };
  }
}

function AuditDialog({ t, open, onClose, title, events, loading }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress size={24} />
        ) : events.length === 0 ? (
          <Typography color="text.secondary">{t("publishResults.noAuditEvents")}</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("publishResults.auditWhen")}</TableCell>
                  <TableCell>{t("publishResults.auditFederation")}</TableCell>
                  <TableCell>{t("publishResults.auditAction")}</TableCell>
                  <TableCell>{t("publishResults.auditOutcome")}</TableCell>
                  <TableCell>{t("publishResults.auditMessage")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((ev) => (
                  <TableRow key={ev.id}>
                    <TableCell>{ev.createdAt ? new Date(ev.createdAt).toLocaleString() : ""}</TableCell>
                    <TableCell>{ev.federation}</TableCell>
                    <TableCell>{ev.action}</TableCell>
                    <TableCell>{ev.outcome}</TableCell>
                    <TableCell>{ev.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("publishResults.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PublishResults() {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  // #1092 — activeShowLoading gates the missing-USEF-config alerts below:
  // while the ActiveShowProvider is still fetching the show list,
  // activeShow is null (not yet "genuinely absent"), so usefInfoReady
  // reading false shouldn't be shown as an error yet.
  const { activeShowId, activeShow, loading: activeShowLoading } = useActiveShow();
  const fed = useFederationResults(activeShowId, activeShow?.usefCompId, activeShow?.usefCompYear);

  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  useEffect(() => {
    if (!activeShowId) {
      setClasses([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingClasses(true);
        const list = unwrap(await scoreApi.classes.listClasses(activeShowId));
        if (!cancelled) setClasses(list);
      } catch {
        if (!cancelled) setClasses([]);
      } finally {
        if (!cancelled) setLoadingClasses(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId, scoreApi]);
  const classesById = useMemo(() => {
    const map = {};
    for (const c of classes) map[c.id] = c;
    return map;
  }, [classes]);

  // #1011 — ride count per class, straight from the class's own rides (GET
  // /ride?classId=, same call ShowDashboard.jsx's loadShowAggregate makes),
  // NOT from the full federation preview assemble. rides.length counts every
  // ride regardless of status, so a SCRATCHED ride counts too — matching
  // USEF's TotalEntries (usef-serializer.js:256, results.length before any
  // scratch filtering). Also backs "hide empty classes" below: Manager hides
  // 0-ride ("Did not Fill") classes entirely (UploadResultsPage.jsx:747-751);
  // same rule here, with an opt-in "show empty" toggle.
  const [rideCountByClassId, setRideCountByClassId] = useState({});
  // #1042 — the same per-class GET /ride results, kept in full (not just
  // .length) so classIncompleteInfo can read each ride's raw status/placing
  // without a second fetch.
  const [ridesByClassId, setRidesByClassId] = useState({});
  const [loadingRideCounts, setLoadingRideCounts] = useState(false);
  // #1083 — key this refetch on the class ID SET, not the `classes` array
  // reference. handleToggleSkip's setClasses(prev => prev.map(...)) (and any
  // other single-row patch) replaces the whole array on every call, which
  // used to re-trigger this effect on an unrelated field flip (e.g.
  // skipUsefUpload) — re-fetching listRides for every class and, via
  // loadingRideCounts, swapping the whole table for a CircularProgress
  // (below), which read as the entire table reloading/reflowing and lost
  // scroll position + focus. classIds only changes when classes are actually
  // added, removed, or reordered.
  const classIds = useMemo(() => classes.map((c) => c.id).join(","), [classes]);
  useEffect(() => {
    if (!classes.length) {
      setRideCountByClassId({});
      setRidesByClassId({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingRideCounts(true);
      try {
        const entries = await Promise.all(
          classes.map(async (c) => {
            try {
              const rides = unwrap(await scoreApi.rides.listRides(c.id));
              return [c.id, rides];
            } catch {
              return [c.id, []];
            }
          }),
        );
        if (!cancelled) {
          setRideCountByClassId(Object.fromEntries(entries.map(([id, rides]) => [id, rides.length])));
          setRidesByClassId(Object.fromEntries(entries));
        }
      } finally {
        if (!cancelled) setLoadingRideCounts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally classIds not classes, see #1083 comment above
  }, [classIds, scoreApi]);

  // #1042 — derived (CONFIRMED/FINAL/...) status per ride, needed to tell a
  // truly closed ride apart from one merely COMPLETED (signed by some but not
  // all expected judges, #791) — same distinction #340's classNeedsPlacings
  // already draws. One batched POST /ride/status-batch call across every
  // class's rides (mirrors useClassesNeedingPlacings.js, no per-class fan-out).
  const [rideStatusByRideId, setRideStatusByRideId] = useState({});
  // #1092 — this fetch previously had no loading flag at all: once
  // ridesByClassId resolved (loadingRideCounts -> false) the class table
  // rendered immediately, but classIncompleteInfo (used by
  // classDerivedState below) reads rideStatusByRideId — while THIS fetch was
  // still in flight it read as {}, so every open ride looked unresolved and
  // classes flashed an "Incomplete" pill even when the real status (e.g.
  // already published) hadn't loaded yet. loadingRideStatus closes that gap.
  const [loadingRideStatus, setLoadingRideStatus] = useState(false);
  useEffect(() => {
    const ids = [...new Set(Object.values(ridesByClassId).flat().map((r) => r.id).filter(Boolean))];
    if (!ids.length) {
      setRideStatusByRideId({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingRideStatus(true);
      try {
        const rows = unwrap(await scoreApi.rides.batchRideStatus({ rideIds: ids }));
        if (!cancelled) setRideStatusByRideId(Object.fromEntries(rows.map((r) => [r.rideId, r.status])));
      } catch {
        if (!cancelled) setRideStatusByRideId({});
      } finally {
        if (!cancelled) setLoadingRideStatus(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ridesByClassId, scoreApi]);

  // #1011 — per-class "USEF returned errors from the last upload" (#1010's
  // classification.resultErrors/classError), keyed by classId. Neither
  // getFederationShowState nor the class-state row persists this server-side
  // (federation-submission-state.js only commits state on a SUCCESS
  // classification) — it only ever exists in the immediate upload/correct
  // response, so this page tracks it locally as actions resolve: a SUCCESS
  // (or a subsequent action with no errors) clears the class's entry, any
  // resultErrors/classError sets it. This is session-scoped (a page reload
  // loses it) — the only source of truth is the response of the last
  // upload/correct call made from this page.
  const [classErrorsByClassId, setClassErrorsByClassId] = useState({});
  const recordClassClassification = (classId, classification) => {
    setClassErrorsByClassId((prev) => {
      const hasError = !!(classification && (classification.resultErrors?.length || classification.classError));
      if (!hasError) {
        if (!(classId in prev)) return prev;
        const next = { ...prev };
        delete next[classId];
        return next;
      }
      return { ...prev, [classId]: classification };
    });
  };
  // #1103 AC3 — normalizes the TWO distinct shapes an upload/correct call can
  // fail with into the one classification-like shape recordClassClassification/
  // the class-detail panel already knows how to render:
  //   - 409 usef_rejected: err.raw.classification = {outcome, message,
  //     resultErrors[], classError} — per-ride USEF rejection detail.
  //   - 400 usef_validation_error: err.raw has NO classification at all, just
  //     {message, issues[]} (missing judge/rider USEF ids, caught before the
  //     call ever reaches USEF) — without this, that response would carry no
  //     classError/resultErrors and silently fail to register as an error.
  const classificationFromError = (err) => {
    if (err?.raw?.classification) return err.raw.classification;
    // #1107 — structuredIssues rides alongside issues[] (usef-serializer.js's
    // parallel array) so the class-detail panel can group by ride/CNO.
    if (err?.raw?.issues?.length) {
      return {
        classError: err.raw.message,
        issues: err.raw.issues,
        structuredIssues: err.raw.structuredIssues || [],
        resultErrors: [],
      };
    }
    if (err?.raw?.message) return { classError: err.raw.message, resultErrors: [] };
    return null;
  };
  // #1096 — returns the classification so callers that need to tally the
  // outcome (doPublishUsef's post-walk publishSummary) don't have to re-read
  // classErrorsByClassId state, which won't have flushed between awaits in
  // the same walk.
  const handleUpload = async (classId, classRow) => {
    try {
      const res = await fed.uploadClass(classId, classRow);
      recordClassClassification(classId, res?.classification);
      return res?.classification;
    } catch (err) {
      const classification = classificationFromError(err);
      recordClassClassification(classId, classification);
      return classification;
    }
  };

  // #1039 — the one deliberate per-row action left: re-push an
  // already-published (green) class. deleteUsefClass first (UPLOADED ->
  // DRAFT is the only legal pre-submit reversal), then re-upload. If the
  // delete fails, actionError surfaces it and the upload is never attempted.
  // #1103 — takes just the classId; the class row (needed to rebuild the
  // upload body) is looked up fresh from classesById at call time.
  const handleRepublish = async (classId) => {
    try {
      await fed.deleteClass(classId, classesById[classId]?.classNumber);
    } catch {
      return;
    }
    await handleUpload(classId, classesById[classId]);
  };

  // #1040 — the post-submit counterpart to handleRepublish: once a class is
  // SUBMITTED/CORRECTED there's no delete+reupload (ALLOWED_TRANSITIONS has
  // no SUBMITTED/CORRECTED -> DRAFT edge), so an operator who's edited a
  // scoresheet after finalize resubmits the whole class via
  // correctUsefClass/submitcorrectedclass instead. Same classification
  // bookkeeping as handleUpload.
  const handleCorrect = async (classId) => {
    try {
      const res = await fed.correctClass(classId, classesById[classId]);
      recordClassClassification(classId, res?.classification);
    } catch (err) {
      recordClassClassification(classId, classificationFromError(err));
    }
  };

  // #1041 AC3 — operator override, independent of the automatic fingerprint
  // compare. No classification bookkeeping needed (doesn't touch USEF) —
  // withAction's reloadClassStates already picks up the new stale flag.
  const handleMarkForRepublish = async (classId) => {
    try {
      await fed.markForRepublish(classId);
    } catch {
      // surfaced via fed.actionError
    }
  };

  // #1103 — the show-level Publish walk, and the definition of "ready to
  // publish" (AC1/AC4): every class that isn't operator-skipped, isn't
  // already published (UPLOADED/SUBMITTED/CORRECTED), and isn't Incomplete
  // (classIncompleteInfo — not placed, or has open rides). No preview run
  // required — walks `classes` (the score-class rows) directly, since
  // buildClassBody (useFederationResults.js) now sources classNumber/
  // sectionCd/classTitle straight off the class row. Already-published
  // classes are left alone; re-publishing one is the per-row action.
  //
  // #1038 — Publish is upload-only and reversible: it walks this same list
  // through the per-row handleUpload each row's own "Publish"/"Re-publish"
  // action uses (opening the USEF competition internally on the first upload
  // — no separate "open" call), leaving classes UPLOADED (green). It never
  // calls the combined upload+submit endpoint — that would also submit the
  // competition, which LOCKS it. The one-way submit/lock is Finalize, below.
  const publishableClasses = useMemo(() => {
    return classes.filter((c) => {
      if (c.skipUsefUpload === true) return false;
      if (!classCanUpload(fed.stateByClassId[c.id])) return false;
      return !classIncompleteInfo(ridesByClassId[c.id], rideStatusByRideId);
    });
  }, [classes, fed.stateByClassId, ridesByClassId, rideStatusByRideId]);

  // #1103 — the 5-state model (card's pre-finalize lane): every class is in
  // exactly ONE of incomplete/publishing/error/published/ready, independent
  // of skip (its own flag, rendered separately — no longer collapses the
  // state) and stale (also its own flag, only meaningful once published —
  // see statusBadge above). Order matters: an in-flight upload (actionBusy)
  // wins over everything else since it's the freshest signal; a hasError
  // classification wins over the raw federation status next (a class can be
  // UPLOADED-with-a-later-failed-correct-attempt, in which case the error is
  // the more urgent thing to surface); UPLOADED/SUBMITTED/CORRECTED are all
  // "published" (the correction-vs-plain-upload distinction still shows up
  // in the class-detail panel below, off the raw status); "incomplete" only
  // applies pre-upload (not placed, or has open rides); anything left is
  // "ready to publish".
  const classDerivedState = (c) => {
    if (fed.actionBusy === c.id) return "publishing";
    const classification = classErrorsByClassId[c.id];
    const hasError = !!(classification && (classification.resultErrors?.length || classification.classError));
    if (hasError) return "error";
    const status = fed.stateByClassId[c.id];
    if (status === "UPLOADED" || status === "SUBMITTED" || status === "CORRECTED") return "published";
    if (classIncompleteInfo(ridesByClassId[c.id], rideStatusByRideId)) return "incomplete";
    return "ready";
  };
  const STATE_SORT_ORDER = {
    incomplete: 0,
    ready: 1,
    publishing: 2,
    error: 3,
    published: 4,
  };

  // #1013 — persist the skip-USEF-upload override on score-class (score-api's
  // setClassSkipUsefUpload, reusing the #1005 read-modify-write field-write
  // pattern class-config.js's other per-class writes already use). Updates
  // the local `classes` list from the response so the table/derived state
  // reflect the new value immediately, without a full reload.
  const [togglingSkipClassId, setTogglingSkipClassId] = useState(null);
  const [skipToggleError, setSkipToggleError] = useState(null);
  const handleToggleSkip = async (classId, skipUsefUpload) => {
    setTogglingSkipClassId(classId);
    setSkipToggleError(null);
    try {
      const updated = unwrap(await scoreApi.classes.setClassSkipUsefUpload(classId, { skipUsefUpload }));
      setClasses((prev) => prev.map((cl) => (cl.id === classId ? { ...cl, ...updated } : cl)));
    } catch (err) {
      setSkipToggleError(err);
    } finally {
      setTogglingSkipClassId(null);
    }
  };

  // #1012 — full per-class federation-submission-state row (not just its
  // status), keyed by classId, straight off fed.classStates (#1011's
  // stateByClassId already reduces this to just `.status`). commitClassTransition
  // (report-api's federation-submission-state.js) stamps `updatedAt` only on a
  // SUCCESS classification, so for a class currently UPLOADED/SUBMITTED/
  // CORRECTED this row's updatedAt IS the timestamp of that last successful
  // upload/correct/submit.
  const classStateById = useMemo(() => {
    const map = {};
    for (const row of fed.classStates) map[row.classId] = row;
    return map;
  }, [fed.classStates]);

  // #1012 — per-class expand/collapse for the upload-detail panel (Manager's
  // ResultGroupsPage.jsx Collapse-row pattern). Session-scoped UI state only.
  const [expandedClassIds, setExpandedClassIds] = useState(() => new Set());
  const toggleClassDetail = (classId) => {
    setExpandedClassIds((prev) => {
      const next = new Set(prev);
      if (next.has(classId)) next.delete(classId);
      else next.add(classId);
      return next;
    });
  };

  // #1011 — house filter/sort pattern (ClassManager.jsx / RideEditor.jsx /
  // PeopleManager.jsx): search box (substring on class number/name) + a
  // "show empty" and "errors only" Switch, applied before sort. Sort by class
  // number is the default (kept stable — sortByClassNumber is a stable sort,
  // so re-sorting its output by derived state preserves class-number order
  // within each state bucket).
  const [search, setSearch] = useState("");
  const [showEmptyClasses, setShowEmptyClasses] = useState(false);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [sortBy, setSortBy] = useState("number");
  const visibleClasses = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = classes.filter((c) => {
      // #1011 AC1 — hide 0-ride classes by default (Manager's "(Did not
      // Fill)"/SKIPPED rule, UploadResultsPage.jsx:747-751). Only hide once
      // the count is actually known (0), never while still loading
      // (undefined), so classes don't flash-hide-then-reappear.
      if (!showEmptyClasses && rideCountByClassId[c.id] === 0) return false;
      if (errorsOnly && classDerivedState(c) !== "error") return false;
      if (q && !`${classNumberLabel(c)} ${c.className ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const numberSorted = sortByClassNumber(filtered);
    if (sortBy === "state") {
      return [...numberSorted].sort(
        (a, b) => STATE_SORT_ORDER[classDerivedState(a)] - STATE_SORT_ORDER[classDerivedState(b)],
      );
    }
    return numberSorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- classDerivedState/STATE_SORT_ORDER are stable per-render derivations of classErrorsByClassId/fed.stateByClassId/ridesByClassId/rideStatusByRideId, all already listed
  }, [
    classes,
    search,
    showEmptyClasses,
    errorsOnly,
    sortBy,
    rideCountByClassId,
    classErrorsByClassId,
    fed.stateByClassId,
    ridesByClassId,
    rideStatusByRideId,
  ]);

  // #751 — score-show has no stored competition end date; derive the last
  // scheduled day across the show's classes (same day-key derivation
  // ReportsPage/#634 uses) as the USEF 10-day-deadline clock start.
  //
  // #1909 — prefer the show's per-show summary row's dayKeys (already
  // ascending, so the last entry is the latest day) over reducing the class
  // list; a one-shot page-load computation, so summary's reconcile-cadence
  // freshness is fine. Falls back to the class-list reduction when summary is
  // absent (not yet re-reconciled).
  const competitionEndDate = useMemo(() => {
    if (activeShow?.summary?.dayKeys?.length) {
      const keys = activeShow.summary.dayKeys;
      return keys[keys.length - 1];
    }
    const keys = classes.map(classDayKey).filter(Boolean).sort();
    return keys.length ? keys[keys.length - 1] : null;
  }, [activeShow?.summary, classes]);
  useEffect(() => {
    if (competitionEndDate) fed.loadDeadline(competitionEndDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadDeadline identity changes with showId/reportApi, not what should re-trigger this
  }, [competitionEndDate, activeShowId]);

  // #1007 — comp year + USEF comp id are read-only, sourced from the show
  // record (#1004-#1006 stamp usefCompYear/usefCompId onto score-show at
  // reconcile). No operator setup step; "ready" just means the show has been
  // reconciled with that USEF data.
  const usefInfoReady = !!(activeShow?.usefCompYear && activeShow?.usefCompId);

  const [auditDialog, setAuditDialog] = useState(null); // { title, classId } | { title, show: true }
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const openClassAudit = async (classId, label) => {
    setAuditDialog({ title: t("publishResults.classAuditTitle", { label }) });
    setAuditLoading(true);
    try {
      setAuditEvents(await fed.loadClassAudit(classId));
    } finally {
      setAuditLoading(false);
    }
  };
  const openShowAudit = async () => {
    setAuditDialog({ title: t("publishResults.showAuditTitle") });
    setAuditLoading(true);
    try {
      setAuditEvents(await fed.loadShowAudit());
    } finally {
      setAuditLoading(false);
    }
  };

  const [confirmPublish, setConfirmPublish] = useState(null); // "usef" | "usdf" | null
  const [confirmChecked, setConfirmChecked] = useState(false);

  // #1038 — Finalize's own one-way-trip confirm, parallel to confirmPublish/
  // confirmChecked above but kept separate: Finalize's dialog carries a
  // second, conditional "skipped class has an error, continue anyway?"
  // checkbox that Publish's dialog doesn't need.
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [finalizeChecked, setFinalizeChecked] = useState(false);
  const [finalizeContinueAnyway, setFinalizeContinueAnyway] = useState(false);

  // #1103 — auto-open a row's dropdown the moment its upload errors (so the
  // API's returned errors, AC3, are visible without an extra click) —
  // additive only, never collapses a row staff already opened by hand.
  // Replaces the old preview-issue auto-open (#1076), which no longer
  // applies since there's no preview issue to detect ahead of publish.
  useEffect(() => {
    const erroredClassIds = Object.keys(classErrorsByClassId);
    if (erroredClassIds.length === 0) return;
    setExpandedClassIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const classId of erroredClassIds) {
        if (!next.has(classId)) {
          next.add(classId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [classErrorsByClassId]);

  // #1038 — the show-level walk is a sequence of per-class handleUpload
  // calls, so fed.actionBusy cycles through individual classIds rather than
  // a single "show-usef" key while it runs. publishBusy tracks the walk as a
  // whole for the Publish button/dialog's own busy state.
  const [publishBusy, setPublishBusy] = useState(false);
  // #1096 — the Publish-result banner's data: the post-walk published/error
  // split, tallied from each handleUpload's returned classification (not
  // classErrorsByClassId, which is state and wouldn't reflect this walk's
  // later classes until a re-render). null before any walk has run; cleared
  // at the start of each walk so a fresh Publish always starts from a clean
  // banner.
  const [publishSummary, setPublishSummary] = useState(null);
  // #1103 — AC2: walks publishableClasses (non-skipped, ready-to-publish
  // classes) straight into the upload API, per classId — no preview run.
  const doPublishUsef = async () => {
    setPublishBusy(true);
    setPublishSummary(null);
    try {
      let published = 0;
      let errors = 0;
      for (const c of publishableClasses) {
        const classification = await handleUpload(c.id, c);
        const hasError = !!(classification && (classification.resultErrors?.length || classification.classError));
        if (hasError) errors += 1;
        else published += 1;
      }
      setPublishSummary({ published, errors });
      setConfirmPublish(null);
      setConfirmChecked(false);
    } finally {
      setPublishBusy(false);
    }
  };
  // #1086 — deliver: "email" (unchanged, uses the #1056 recipient) or
  // "download": report-api returns the SAME generated .dat content instead
  // of emailing it (usdf-erss-publish.js's one serializer call, either way),
  // so this just triggers a browser download of what came back.
  const doPublishUsdf = async (deliver) => {
    try {
      const result = await fed.publishUsdf(deliver);
      if (deliver === "download" && result?.datContent) {
        triggerBrowserDownload(result.filename || "usdf-erss.dat", result.datContent);
      }
      setConfirmPublish(null);
      setConfirmChecked(false);
    } catch {
      // surfaced via fed.actionError
    }
  };

  // #1038 — Finalize: submits the WHOLE show in one call (fed.submitUsef,
  // #1047's submit-only endpoint), not per class. Enabled only once every
  // non-skipped class is "published" (green) — and at least one such class
  // exists to submit. #1103 — walks `classes` directly rather than a
  // preview-derived list.
  const nonSkippedClasses = useMemo(() => classes.filter((c) => c.skipUsefUpload !== true), [classes]);
  const finalizeBlockingClasses = useMemo(() => {
    return nonSkippedClasses.filter((c) => classDerivedState(c) !== "published");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- classDerivedState is a stable per-render derivation of classErrorsByClassId/fed.stateByClassId/fed.actionBusy/ridesByClassId/rideStatusByRideId, all already listed
  }, [nonSkippedClasses, classErrorsByClassId, fed.stateByClassId, fed.actionBusy, ridesByClassId, rideStatusByRideId]);
  const canFinalize = finalizeBlockingClasses.length === 0 && nonSkippedClasses.length > 0;

  // #1038 AC4 — a skipped class can carry an error from an earlier upload
  // attempt (classErrorsByClassId isn't cleared by toggling skip) — the
  // Finalize dialog warns about these and requires an explicit "continue
  // anyway" before submitting, since Finalize never retries skipped classes.
  const skippedClassesWithErrors = useMemo(() => {
    return classes.filter((c) => c.skipUsefUpload === true && classErrorsByClassId[c.id]);
  }, [classes, classErrorsByClassId]);

  // #1038 AC5 — the show is locked once any class has actually been
  // submitted/corrected (SUBMITTED/CORRECTED is the ALLOWED_TRANSITIONS
  // end state — federation-submission-state.js never reverses it), which
  // only happens via Finalize. Used to disable Publish and hide/disable
  // Finalize after the fact.
  const showIsSubmitted = useMemo(() => {
    return fed.classStates.some((row) => row.status === "SUBMITTED" || row.status === "CORRECTED");
  }, [fed.classStates]);

  const doFinalizeUsef = async () => {
    try {
      const res = await fed.submitUsef(nonSkippedClasses);
      for (const cs of res?.classStates || []) recordClassClassification(cs.classId, cs.classification);
      setConfirmFinalize(false);
      setFinalizeChecked(false);
      setFinalizeContinueAnyway(false);
    } catch (err) {
      for (const cs of err?.raw?.classStates || []) recordClassClassification(cs.classId, cs.classification);
      // surfaced via fed.actionError
    }
  };

  // #1092 — a class row's status pill depends on fed.stateByClassId
  // (fed.statesLoading), ridesByClassId/rideCountByClassId (loadingRideCounts),
  // and rideStatusByRideId (loadingRideStatus) — classDerivedState reads all
  // three. Any of them still in flight means the derived state (and so the
  // pill label) isn't trustworthy yet, so each row renders a spinner instead
  // of a real Chip until every one has resolved.
  const classPillLoading = fed.statesLoading || loadingRideCounts || loadingRideStatus;

  // #1092 — the missing-USEF-config alerts below are only meaningful once
  // the active show has actually resolved: while activeShowLoading is true,
  // activeShow (and so usefCompYear/usefCompId) is null-because-not-fetched,
  // not null-because-genuinely-unset, so usefInfoReady reading false isn't
  // an error yet — it's just "don't know yet".
  const usefInfoUnresolved = !activeShowLoading && !usefInfoReady;

  // #2195 — activeShowLoading (aliased loading: activeShowLoading above)
  // covers the show-LIST fetch: while it's true, activeShowId is null
  // because the fetch hasn't resolved yet, not because the org genuinely
  // has no shows. Rendering the noShow text on that in-flight null told the
  // operator there was nothing to publish before we actually knew that.
  if (!activeShowId) {
    return (
      <Box sx={{ p: 2 }}>
        {activeShowLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress data-testid="publish-results-page-show-loading" />
          </Box>
        ) : (
          <Typography color="text.secondary">{t("reports.noShow")}</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }} data-testid="publish-results-page">
      <Typography variant="h5">
        {t("publishResults.title")}
        {activeShow?.name ? ` — ${activeShow.name}` : ""}
      </Typography>

      {/* #1054 — no distinct USDF deadline exists to mirror: docs/score/
          federation-results-publishing.md's 10-day window is specifically a
          USEF GR rule, and there's no USDF-specific submission-window rule
          or field anywhere in report-api (federation-deadline.js only
          computes the one USEF window; usdf-erss-publish.js has no deadline
          concept at all — a USDF ERSS is just emailed whenever Publish to
          USDF is clicked). This banner already renders above BOTH Publish
          buttons unconditionally (fed.deadline comes from competitionEndDate
          alone, not from usefInfoReady or which federation button is about
          to be clicked), so per the card's "or confirmed N/A" — it's kept as
          ONE banner covering both, with copy updated to say so, rather than
          fabricating a second USDF-only deadline computation. */}
      {fed.deadline && (
        <Alert
          severity={fed.deadline.isExpired ? "error" : fed.deadline.daysRemaining <= 3 ? "warning" : "info"}
          data-testid="deadline-banner"
        >
          {fed.deadline.isExpired
            ? t("publishResults.deadlineExpired", { deadline: new Date(fed.deadline.deadline).toLocaleDateString() })
            : t("publishResults.deadlineRemaining", {
                days: fed.deadline.daysRemaining,
                deadline: new Date(fed.deadline.deadline).toLocaleDateString(),
              })}
        </Alert>
      )}

      {/* #1075 — AC3 publish action moved to the TOP of the page (was after
          the class list + preview) so staff can reach Publish/Finalize
          without scrolling past the class table. Behavior/handlers/disabled
          logic unchanged — only relocated. */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t("publishResults.publishTitle")}
        </Typography>
        {fed.actionError && (fed.actionError.key === "show-usef" || fed.actionError.key === "show-usdf") && (
          <Alert severity="error" sx={{ mb: 1 }} data-testid="publish-error-alert">
            {fed.actionError.message}
            {fed.actionError.issues && (
              <ul>
                {fed.actionError.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            )}
            {fed.actionError.issuesByClass &&
              Object.entries(fed.actionError.issuesByClass).map(([classId, issues]) => (
                <Box key={classId}>
                  <Typography variant="subtitle2">{classId}</Typography>
                  <ul>
                    {issues.map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </Box>
              ))}
          </Alert>
        )}
        {fed.actionError && fed.actionError.key === "submit-usef" && (
          <Alert severity="error" sx={{ mb: 1 }} data-testid="finalize-error-alert">
            {fed.actionError.message}
            {fed.actionError.issues && (
              <ul>
                {fed.actionError.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        {/* #1038 AC5 — once the show is submitted/corrected, Publish is done
            and Finalize has nothing left to do; further change is a
            correction (#1040), not a re-publish. */}
        {showIsSubmitted && (
          <Alert severity="info" sx={{ mb: 1 }} data-testid="show-submitted-notice">
            {t("publishResults.showSubmittedNotice")}
          </Alert>
        )}
        {/* #1040 AC4 — USDF has no correction API (docs/score/
            federation-results-publishing.md D-E/§1.2) — unlike USEF's
            submitcorrectedclass, a USDF fix is a manual re-email of the .dat
            file outside this page. Deliberately out of scope here; this note
            is the only UI acknowledgment. */}
        {showIsSubmitted && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
            data-testid="usdf-correction-note"
          >
            {t("publishResults.usdfCorrectionNote")}
          </Typography>
        )}
        {/* #1103 — the last Publish walk's result: success or partial
            (some classes errored — review them below, fix, and Publish
            again). No pre-walk "ready"/"needs preview" banner any more —
            there's no gate to report on, Publish is just enabled once
            >=1 class is ready to publish (below). */}
        {!showIsSubmitted && publishSummary && (
          <Alert
            severity={publishSummary.errors === 0 ? "success" : "warning"}
            sx={{ mb: 1 }}
            data-testid="publish-result-banner"
          >
            {publishSummary.errors === 0
              ? t("publishResults.publishResultSuccess", { count: publishSummary.published })
              : t("publishResults.publishResultPartial", {
                  published: publishSummary.published,
                  errors: publishSummary.errors,
                })}
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* #1103 AC1/AC2 — no preview gate: enabled whenever at least one
                class is ready to publish (publishableClasses), full stop. */}
            <Button
              variant="contained"
              color="primary"
              disabled={
                !usefInfoReady || publishableClasses.length === 0 || showIsSubmitted || fed.actionBusy === "show-usef"
              }
              onClick={() => setConfirmPublish("usef")}
              data-testid="publish-usef-button"
              startIcon={fed.actionBusy === "show-usef" ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {t("publishResults.publishUsef")}
            </Button>
            {/* #1038 — Finalize sits next to Publish and submits the whole show
                in one call; disabled/hidden once already locked. */}
            <Button
              variant="contained"
              color="warning"
              disabled={!canFinalize || showIsSubmitted || fed.actionBusy === "submit-usef"}
              onClick={() => setConfirmFinalize(true)}
              data-testid="finalize-usef-button"
              startIcon={fed.actionBusy === "submit-usef" ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {t("publishResults.finalizeUsef")}
            </Button>
          </Box>
          {/* #1114 — USDF publish stands on its own, pushed to the far right
              and matching the USEF/Finalize button color (was purple
              color="secondary", visually inconsistent with its neighbors). */}
          <Button
            variant="contained"
            color="primary"
            disabled={!usefInfoReady || fed.actionBusy === "show-usdf"}
            onClick={() => setConfirmPublish("usdf")}
            data-testid="publish-usdf-button"
            startIcon={fed.actionBusy === "show-usdf" ? <CircularProgress size={20} color="inherit" /> : undefined}
            sx={{ ml: "auto" }}
          >
            {t("publishResults.publishUsdf")}
          </Button>
        </Box>
      </Paper>

      {/* AC1 — comp year + USEF comp id, read-only from the show record */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t("publishResults.setupTitle")}
        </Typography>
        <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="body2" data-testid="comp-year-display">
            {t("publishResults.compYear")}: {activeShow?.usefCompYear ?? t("publishResults.notAvailable")}
          </Typography>
          <Typography variant="body2" data-testid="usef-comp-id-display">
            {t("publishResults.usefCompId")}: {activeShow?.usefCompId ?? t("publishResults.notAvailable")}
          </Typography>
          {/* #1092 — usefInfoUnresolved (not the raw !usefInfoReady) so this
              doesn't flash a "not reconciled" error while activeShow is
              simply still loading — see usefInfoUnresolved above. */}
          {usefInfoUnresolved && (
            <Alert severity="warning" data-testid="usef-info-missing-alert" sx={{ py: 0 }}>
              {t("publishResults.usefInfoMissing")}
            </Alert>
          )}
          {/* #1054 — USDF's equivalent of "info not ready": report-api's
              publishUsdf sends this exact compYear/usefCompId pair (there's
              no separate usdfCompId anywhere — useFederationResults.js's
              publishUsdf builds its body from the same two fields as
              publishUsef), so usefInfoReady genuinely IS usdfInfoReady. Kept
              as its own alert (not folded into the USEF one above) so it's
              independently visible/testable next to the USDF publish path. */}
          {usefInfoUnresolved && (
            <Alert severity="warning" data-testid="usdf-info-missing-alert" sx={{ py: 0 }}>
              {t("publishResults.usdfInfoMissing")}
            </Alert>
          )}
        </Box>
      </Paper>

      {/* AC1 — per-class section (read-only) + AC4 status/corrections */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h6" gutterBottom>
            {t("publishResults.classesTitle")}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ color: "text.primary", borderColor: "text.secondary" }}
            startIcon={<HistoryIcon />}
            onClick={openShowAudit}
            data-testid="show-audit-button"
          >
            {t("publishResults.showHistory")}
          </Button>
        </Box>
        {/* #1013 — surfaces a failed skip-toggle save (score-api update); the
            Switch itself reverts to the persisted value on the next render
            since it's controlled off `classes`, not local state. */}
        {skipToggleError && (
          <Alert severity="error" sx={{ mb: 1 }} data-testid="skip-toggle-error">
            {t("publishResults.skipToggleError")}
          </Alert>
        )}
        {/* #1011 — house filter/sort controls (ClassManager.jsx pattern):
            search + sort-by + show-empty/errors-only switches. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2, mt: 1 }}
          alignItems={{ sm: "center" }}
          flexWrap="wrap"
        >
          <TextField
            size="small"
            placeholder={t("publishResults.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="publish-results-search"
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="publish-results-sort-label">{t("publishResults.sortBy")}</InputLabel>
            <Select
              labelId="publish-results-sort-label"
              label={t("publishResults.sortBy")}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="publish-results-sort-select"
            >
              <MenuItem value="number">{t("publishResults.sortNumber")}</MenuItem>
              <MenuItem value="state">{t("publishResults.sortState")}</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showEmptyClasses}
                onChange={(e) => setShowEmptyClasses(e.target.checked)}
                data-testid="show-empty-classes-switch"
              />
            }
            label={t("publishResults.showEmptyClasses")}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={errorsOnly}
                onChange={(e) => setErrorsOnly(e.target.checked)}
                data-testid="errors-only-switch"
              />
            }
            label={t("publishResults.errorsOnly")}
          />
        </Stack>
        {/* #1092 — only blank the WHOLE table while the class list itself
            hasn't loaded (there's nothing to iterate yet). Once classes are
            known, render rows immediately and let each row's own status pill
            spin individually (classPillLoading below) — swapping the entire
            table for one spinner on every subsequent state/ride-count/
            ride-status refresh reads as the table reloading and drops scroll
            position/focus (see #1083 above). */}
        {loadingClasses ? (
          <CircularProgress size={24} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("publishResults.colClass")}</TableCell>
                  <TableCell>{t("publishResults.colSection")}</TableCell>
                  <TableCell>{t("publishResults.colRideCount")}</TableCell>
                  <TableCell>{t("publishResults.colSkip")}</TableCell>
                  <TableCell>{t("publishResults.colStatus")}</TableCell>
                  <TableCell>{t("publishResults.colActions")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleClasses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary" variant="body2">
                        {t("publishResults.noClassesMatch")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visibleClasses.map((c) => {
                  const status = fed.stateByClassId[c.id];
                  const stale = !!fed.staleByClassId[c.id];
                  const classification = classErrorsByClassId[c.id];
                  const hasError = !!(
                    classification &&
                    (classification.resultErrors?.length || classification.classError)
                  );
                  // #1092 — while classPillLoading, treat this row's derived
                  // state as unknown (null) rather than computing it off
                  // still-loading data: classDerivedState reads
                  // fed.stateByClassId/ridesByClassId/rideStatusByRideId, and
                  // a wrong guess (e.g. "incomplete" before ride status has
                  // loaded) would drive the Actions column and the expanded
                  // detail panel below into showing the wrong buttons/text
                  // even though the pill itself is (correctly) a spinner. A
                  // null derivedState matches none of the switch branches
                  // below, so those areas just render nothing until real
                  // data is in — same "no pill, no incorrect anything" rule.
                  const derivedState = classPillLoading ? null : classDerivedState(c);
                  const incompleteInfo =
                    derivedState === "incomplete" ? classIncompleteInfo(ridesByClassId[c.id], rideStatusByRideId) : null;
                  const isDetailExpanded = expandedClassIds.has(c.id);
                  const classStateRow = classStateById[c.id];
                  return (
                    <Fragment key={c.id}>
                    <TableRow data-testid={`class-row-${c.id}`}>
                      <TableCell>
                        {/* #1053 — reuse the shared ClassLink (#864) into
                            /classes/:classId so staff can jump straight from
                            publish to that class's rides and fix a
                            placing/score/reopen, then come back (browser
                            back returns here). */}
                        <ClassLink classObj={c} data-testid={`class-link-${c.id}`} />
                      </TableCell>
                      <TableCell data-testid={`section-code-${c.id}`}>{c.sectionCode || ""}</TableCell>
                      <TableCell data-testid={`ride-count-${c.id}`}>{rideCountByClassId[c.id] ?? ""}</TableCell>
                      <TableCell>
                        <Tooltip title={t("publishResults.skipUpload")}>
                          <span>
                            <Switch
                              size="small"
                              checked={c.skipUsefUpload === true}
                              disabled={togglingSkipClassId === c.id}
                              onChange={(e) => handleToggleSkip(c.id, e.target.checked)}
                              data-testid={`skip-switch-${c.id}`}
                              inputProps={{ "aria-label": t("publishResults.skipUpload") }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {(() => {
                            // #1092 — while any of the three data sources
                            // classDerivedState reads (fed state, ride
                            // counts, ride status) is still loading, the
                            // derived state isn't real yet — render a
                            // spinner instead of a Chip so no pill ever
                            // shows a status (e.g. "Incomplete") based on
                            // not-yet-loaded data.
                            if (classPillLoading) {
                              return (
                                <CircularProgress
                                  size={16}
                                  data-testid={`status-chip-loading-${c.id}`}
                                  aria-label={t("publishResults.statusChipLoading")}
                                />
                              );
                            }
                            const badge = statusBadge(t, derivedState, stale);
                            // #1103 — a distinct data-testid for the stale (published +
                            // out-of-date) badge, never reuses status-chip-*, so a Cypress
                            // spec can target it specifically.
                            const isStaleBadge = derivedState === "published" && stale;
                            const chip = (
                              <Chip
                                size="small"
                                variant={badge.variant || "filled"}
                                label={badge.label}
                                color={badge.color}
                                data-testid={isStaleBadge ? `stale-chip-${c.id}` : `status-chip-${c.id}`}
                              />
                            );
                            return (
                              <>
                                {hasError ? (
                                  <Tooltip
                                    title={
                                      classification.classError ||
                                      (classification.resultErrors || []).map((e) => e.message).join(" ")
                                    }
                                  >
                                    {chip}
                                  </Tooltip>
                                ) : (
                                  chip
                                )}
                                {/* #1103 AC4 — skip is a separate flag, never folded into
                                    the state chip above: a skipped class still shows its
                                    real underlying state (e.g. Ready to publish), plus this
                                    indicator that it's excluded from Publish All. */}
                                {c.skipUsefUpload === true && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="default"
                                    label={t("publishResults.skippedChip")}
                                    data-testid={`skip-chip-${c.id}`}
                                  />
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {/* #1103 — Re-publish is available on any UPLOADED class
                              (classCanRepublish), stale or not — same delete+reupload
                              action either way. Gated on raw status, not the collapsed
                              derivedState, since stale is now just a flag. */}
                          {status === "UPLOADED" && (
                            <Tooltip title={t("publishResults.republish")}>
                              <span>
                                <IconButton
                                  size="small"
                                  sx={{ color: "text.primary" }}
                                  // #1040 AC2 — delete+reupload is blocked once the show is
                                  // finalized, even in the edge case of a class that's still
                                  // individually UPLOADED (e.g. unskipped after Finalize) —
                                  // post-finalize, every edit goes through a correction instead.
                                  disabled={!classCanRepublish(status) || showIsSubmitted || fed.actionBusy === c.id}
                                  onClick={() => handleRepublish(c.id)}
                                  data-testid={`republish-button-${c.id}`}
                                  aria-label={t("publishResults.republish")}
                                >
                                  <ReplayIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {/* #1040 AC1 — once a class is SUBMITTED/CORRECTED, its edit path
                              is USEF's submitcorrectedclass, not delete+reupload. Shown
                              (stale or not) since a class can be corrected more than once
                              (CORRECTED -> CORRECTED is a legal transition). USDF has no
                              correction API — out of scope here, see the usdf-correction-
                              note below the Publish buttons. */}
                          {(status === "SUBMITTED" || status === "CORRECTED") && (
                            <Tooltip title={t("publishResults.submitCorrection")}>
                              <span>
                                <IconButton
                                  size="small"
                                  sx={{ color: "text.primary" }}
                                  disabled={!classCanCorrect(status) || fed.actionBusy === c.id}
                                  onClick={() => handleCorrect(c.id)}
                                  data-testid={`correct-button-${c.id}`}
                                  aria-label={t("publishResults.submitCorrection")}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {/* #1041 AC3 — Mark for re-publish: an operator override
                              available on any already-published class, forcing the
                              stale flag regardless of the fingerprint compare. Distinct
                              data-testid from the badge/republish/correct buttons above. */}
                          {classCanMarkStale(status) && (
                            <Tooltip title={t("publishResults.markForRepublish")}>
                              <span>
                                <IconButton
                                  size="small"
                                  sx={{ color: "text.primary" }}
                                  disabled={fed.actionBusy === c.id || stale}
                                  onClick={() => handleMarkForRepublish(c.id)}
                                  data-testid={`mark-stale-button-${c.id}`}
                                  aria-label={t("publishResults.markForRepublish")}
                                >
                                  <FlagIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title={t("publishResults.showHistory")}>
                            <span>
                              <IconButton
                                size="small"
                                sx={{ color: "text.primary" }}
                                onClick={() => openClassAudit(c.id, classNumberLabel(c))}
                                data-testid={`class-audit-button-${c.id}`}
                                aria-label={t("publishResults.showHistory")}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t(isDetailExpanded ? "publishResults.hideDetail" : "publishResults.showDetail")}>
                          <IconButton
                            size="small"
                            sx={{ color: "text.primary" }}
                            onClick={() => toggleClassDetail(c.id)}
                            data-testid={`class-detail-toggle-${c.id}`}
                            aria-label={t(isDetailExpanded ? "publishResults.hideDetail" : "publishResults.showDetail")}
                          >
                            {isDetailExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    {/* #1012 — per-class expandable detail: binds ONLY to
                        data already in this component (no new fetch) —
                        classDerivedState + ridesByClassId (#1103 — for the
                        error rows' CNO/rider display, replacing the old
                        preview classResult) + classStateById (fed.classStates'
                        updatedAt, stamped only on a SUCCESS transition). */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                        <Collapse in={isDetailExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 2 }} data-testid={`class-detail-${c.id}`}>
                            {/* #1092 — mirrors the pill: while classPillLoading,
                                derivedState is null (see above), so this would
                                otherwise fall to the final "Ready to publish"
                                default and show a wrong/premature label. */}
                            {classPillLoading ? (
                              <CircularProgress size={16} data-testid={`class-detail-loading-${c.id}`} />
                            ) : derivedState === "error" ? (
                              // #1107 — rewrite of the class error display: a GENERIC
                              // banner (no counts, no raw USEF strings) up top, then a
                              // plain-language breakdown grouped by ride below it. Merges
                              // BOTH shapes the last upload/correct call's classification
                              // can carry — the 400 usef_validation_error's structuredIssues
                              // (parallel to the untouched prose issues[] — usef-serializer.js)
                              // and the 409 usef_rejected's resultErrors (#1010, USEF's own
                              // per-ride rejection text) — via groupClassErrors, above.
                              <>
                                <Alert severity="error" sx={{ mb: 1 }} data-testid={`class-detail-class-error-${c.id}`}>
                                  {t("publishResults.errorBanner")}
                                </Alert>
                                {/* #1110 — restores the manual judge-USEFID override this
                                    page had before #1103 ripped out the preview gate. No
                                    preview round trip anymore, so the signal is the upload/
                                    correct call's own issues[] (a #746 name-lookup miss —
                                    including a combined two-judge box that legitimately has
                                    no single-name match, e.g. firstName "Koch" lastName
                                    "Gurney") rather than a pre-flight scan. Filling this in
                                    and retrying sends judgeOverrides[position].USEFID on the
                                    next upload/correct call (useFederationResults'
                                    buildClassBody), which usef-serializer's buildJudgeEntry
                                    already applies (#751 send-back). Unaffected by #1107 —
                                    still reads the prose issues[], not structuredIssues. */}
                                {(() => {
                                  const judgeIssues = judgeIssuesFromIssues(classification?.issues);
                                  if (judgeIssues.length === 0) return null;
                                  const retryAction =
                                    status === "SUBMITTED" || status === "CORRECTED" ? handleCorrect : handleUpload;
                                  return (
                                    <Alert
                                      severity="warning"
                                      sx={{ mb: 1 }}
                                      data-testid={`judge-missing-usefid-alert-${c.id}`}
                                    >
                                      <Typography variant="body2" sx={{ mb: 1 }}>
                                        {t("publishResults.judgeMissingUsefId", {
                                          names: judgeIssues.map((j) => j.name).join(", "),
                                        })}
                                      </Typography>
                                      {judgeIssues.map((j) => (
                                        <Box
                                          key={j.position}
                                          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                                        >
                                          <Typography variant="body2" sx={{ minWidth: 160 }}>
                                            {j.name} ({j.position})
                                          </Typography>
                                          <TextField
                                            size="small"
                                            label={t("publishResults.judgeUsefId")}
                                            value={fed.judgeOverridesByClassId[c.id]?.[j.position]?.USEFID ?? ""}
                                            onChange={(e) =>
                                              fed.setJudgeOverride(c.id, j.position, { USEFID: e.target.value })
                                            }
                                            data-testid={`judge-usefid-input-${c.id}-${j.position}`}
                                          />
                                        </Box>
                                      ))}
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{ mt: 1 }}
                                        disabled={fed.actionBusy === c.id}
                                        onClick={() => retryAction(c.id, c)}
                                        data-testid={`judge-override-retry-${c.id}`}
                                      >
                                        {t("publishResults.retryUpload")}
                                      </Button>
                                    </Alert>
                                  );
                                })()}
                                {(() => {
                                  const groups = groupClassErrors(classification, ridesByClassId[c.id], t);
                                  if (groups.length === 0) return null;
                                  return (
                                    <Box data-testid={`class-detail-grouped-errors-${c.id}`}>
                                      {groups.map((group) => (
                                        <Box key={group.key} sx={{ mb: 1 }}>
                                          <Typography variant="subtitle2">{group.heading}</Typography>
                                          <ul>
                                            {group.messages.map((message, i) => (
                                              <li key={i}>{message}</li>
                                            ))}
                                          </ul>
                                        </Box>
                                      ))}
                                    </Box>
                                  );
                                })()}
                              </>
                            ) : derivedState === "published" ? (
                              stale ? (
                                <Alert severity="warning" data-testid={`class-detail-stale-${c.id}`}>
                                  {t("publishResults.detailStale")}
                                </Alert>
                              ) : status === "CORRECTED" ? (
                                <Alert severity="success" data-testid={`class-detail-corrected-${c.id}`}>
                                  {t("publishResults.detailCorrectedAt", {
                                    when:
                                      formatVenueLocalDateTime(classStateRow?.updatedAt) ||
                                      t("publishResults.detailWhenUnknown"),
                                  })}
                                </Alert>
                              ) : (
                                <Alert severity="success" data-testid={`class-detail-success-${c.id}`}>
                                  {t("publishResults.detailSuccessAt", {
                                    when:
                                      formatVenueLocalDateTime(classStateRow?.updatedAt) ||
                                      t("publishResults.detailWhenUnknown"),
                                  })}
                                </Alert>
                              )
                            ) : derivedState === "incomplete" ? (
                              <Alert severity="info" data-testid={`class-detail-incomplete-${c.id}`}>
                                {incompleteInfo?.reason === "openRides"
                                  ? t("publishResults.detailIncompleteOpenRides", { count: incompleteInfo.count })
                                  : t("publishResults.detailIncompleteNotPlaced")}
                              </Alert>
                            ) : derivedState === "publishing" ? (
                              <Alert severity="info" data-testid={`class-detail-publishing-${c.id}`}>
                                {t("publishResults.detailPublishing")}
                              </Alert>
                            ) : (
                              <Typography color="text.secondary" variant="body2" data-testid={`class-detail-not-uploaded-${c.id}`}>
                                {t("publishResults.detailReadyToPublish")}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={!!confirmPublish} onClose={() => setConfirmPublish(null)}>
        <DialogTitle>
          {confirmPublish === "usef" ? t("publishResults.confirmUsefTitle") : t("publishResults.confirmUsdfTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {confirmPublish === "usef" ? t("publishResults.confirmUsefBody") : t("publishResults.confirmUsdfBody")}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} />}
            label={t("publishResults.confirmCheckbox")}
            data-testid="confirm-publish-checkbox"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPublish(null)}>{t("publishResults.cancel")}</Button>
          {confirmPublish === "usef" ? (
            <Button
              variant="contained"
              disabled={!confirmChecked || publishBusy}
              onClick={doPublishUsef}
              data-testid="confirm-publish-button"
            >
              {t("publishResults.confirmPublish")}
            </Button>
          ) : (
            // #1086 — USDF offers a CHOICE: email straight to USDF (#1056's
            // per-env recipient), or download the exact same generated .dat
            // to submit themselves. Same confirm-checkbox gate (#1054/#1077)
            // covers both.
            <>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={!confirmChecked || fed.actionBusy === "show-usdf"}
                onClick={() => doPublishUsdf("download")}
                data-testid="confirm-publish-usdf-download-button"
              >
                {t("publishResults.downloadDat")}
              </Button>
              <Button
                variant="contained"
                startIcon={<EmailIcon />}
                disabled={!confirmChecked || fed.actionBusy === "show-usdf"}
                onClick={() => doPublishUsdf("email")}
                data-testid="confirm-publish-usdf-email-button"
              >
                {t("publishResults.emailToUsdf")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* #1038 — Finalize's one-way-trip confirm: distinct from Publish's
          dialog above, with its own checkbox plus a conditional
          skipped-with-error warning + "continue anyway" gate (AC3/AC4). */}
      <Dialog
        open={confirmFinalize}
        onClose={() => {
          setConfirmFinalize(false);
          setFinalizeContinueAnyway(false);
        }}
      >
        <DialogTitle>{t("publishResults.confirmFinalizeTitle")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t("publishResults.confirmFinalizeBody")}</Typography>
          {skippedClassesWithErrors.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="finalize-skipped-errors-warning">
              {t("publishResults.finalizeSkippedErrorsWarning", { count: skippedClassesWithErrors.length })}
            </Alert>
          )}
          <FormControlLabel
            control={
              <Checkbox checked={finalizeChecked} onChange={(e) => setFinalizeChecked(e.target.checked)} />
            }
            label={t("publishResults.confirmFinalizeCheckbox")}
            data-testid="confirm-finalize-checkbox"
          />
          {skippedClassesWithErrors.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={finalizeContinueAnyway}
                  onChange={(e) => setFinalizeContinueAnyway(e.target.checked)}
                />
              }
              label={t("publishResults.finalizeContinueAnyway")}
              data-testid="finalize-continue-anyway-checkbox"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmFinalize(false);
              setFinalizeContinueAnyway(false);
            }}
          >
            {t("publishResults.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={
              !finalizeChecked ||
              (skippedClassesWithErrors.length > 0 && !finalizeContinueAnyway) ||
              fed.actionBusy === "submit-usef"
            }
            onClick={doFinalizeUsef}
            data-testid="confirm-finalize-button"
          >
            {t("publishResults.finalizeUsef")}
          </Button>
        </DialogActions>
      </Dialog>

      <AuditDialog
        t={t}
        open={!!auditDialog}
        onClose={() => setAuditDialog(null)}
        title={auditDialog?.title || ""}
        events={auditEvents}
        loading={auditLoading}
      />
    </Box>
  );
}
